import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, useRequireAuth } from '../lib/auth';
import {
  useDashboardSnapshot,
  type MyWorkspace,
} from '../lib/queries';
import { apiFetch } from '../lib/api';
import { ORG_KINDS } from '../data/orgKinds';


export function DashboardPage() {
  const { user, loading } = useRequireAuth();
  /* One consolidated snapshot request replaces four parallel queries
     (approval-status, admin-check, teams, pending-approvals).
     Backed by dbo.usp_dashboard_snapshot on the server. */
  const snapshotQ = useDashboardSnapshot(user?.id);
  const snapshot = snapshotQ.data;
  const isAdmin = !!snapshot?.is_app_admin;
  const approved = !!snapshot?.is_approved;

  if (loading || snapshotQ.isLoading) {
    return <CenterText>Loading…</CenterText>;
  }
  if (!user) return null;

  // -----------------------------------------------------------
  // Unapproved branch — show pending state, fire notification once.
  // -----------------------------------------------------------
  if (!approved) {
    return (
      <PendingPanel userEmail={user.email ?? ''} />
    );
  }

  // -----------------------------------------------------------
  // Approved branch — normal dashboard.
  // -----------------------------------------------------------
  const workspaces = (snapshot?.workspaces ?? []) as MyWorkspace[];
  const hasWorkspaces = workspaces.length > 0;
  const pendingCount = snapshot?.pending_approvals_count ?? 0;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
      {isAdmin && pendingCount > 0 && (
        <div className="border-l-2 border-accent pl-5 py-3 mb-8 flex items-baseline justify-between gap-4 flex-wrap bg-paper-deep">
          <div>
            <div className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent mb-1">
              Admin
            </div>
            <div className="font-editorial text-[15px] text-ink">
              {pendingCount} {pendingCount === 1 ? 'person is' : 'people are'} waiting for approval.
            </div>
          </div>
          <Link to="/account/admin/approvals" className="btn-text">
            Review →
          </Link>
        </div>
      )}
      {isAdmin && pendingCount === 0 && (
        <div className="mb-8 font-label text-[10px] uppercase tracking-[0.18em] text-ink-muted">
          Admin · <Link to="/account/admin/approvals" className="hover:text-accent">approval queue</Link>
        </div>
      )}

      <div className="eyebrow mb-4">Welcome back</div>
      <h1 className="editorial-h1 mb-3">
        Your <em className="italic text-accent">teams.</em>
      </h1>

      {snapshotQ.error ? (
        <p className="font-editorial italic text-[#a02525] mt-8">
          Couldn't load your teams — {(snapshotQ.error as Error).message}
        </p>
      ) : hasWorkspaces ? (
        <>
          <p className="editorial-deck mb-10 max-w-2xl">
            Pick a team to continue, or create another one — many LEAs run
            separate checklists for different fiscal years or initiatives.
          </p>

          <ul className="space-y-px bg-rule border border-rule mb-10">
            {workspaces.map((w) => (
              <li key={w.id}>
                <WorkspaceRow workspace={w} />
              </li>
            ))}
          </ul>

          <Link to="/account/teams/new" className="btn-text">
            Create another team
          </Link>
        </>
      ) : (
        <>
          <p className="editorial-deck mb-10 max-w-2xl">
            Get started by creating a team for your LEA. You'll be set as
            the owner. Once it's live, you can invite teammates and start your
            CYBHI checklist.
          </p>

          <div className="border border-rule bg-paper-deep p-8 md:p-10 max-w-2xl">
            <div className="font-label text-[11px] uppercase tracking-[0.22em] text-accent mb-3">
              First-time setup
            </div>
            <h2 className="font-editorial text-[28px] font-medium text-ink mb-2 leading-tight">
              Create your first LEA team.
            </h2>
            <p className="font-editorial italic text-[16px] text-ink-soft mb-7 max-w-lg">
              Takes 15 seconds. You'll need a name (e.g. "Alameda COE — CYBHI FY26"
              or just "Our LEA") and a type — that's it.
            </p>
            <Link to="/account/teams/new" className="btn-text">
              Create a team
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Shown to a signed-in user whose profile.approved_at is null. The founder
 * already heard about the sign-up from the server (shared/plan.ts ensureProfile).
 */
function PendingPanel({ userEmail }: { userEmail: string }) {
  const { signOut } = useAuth();

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
      <div className="eyebrow mb-4">Almost there</div>
      <h1 className="editorial-h1 mb-4">
        Your request is <em className="italic text-accent">on the way.</em>
      </h1>
      <p className="editorial-deck mb-10">
        Thanks for confirming your email. Access to the CYBHI Onboarding
        Checklist is approved by hand — Ash will review your request and you'll
        be able to sign in and create teams once it's approved.
      </p>

      <div className="border border-rule bg-paper-deep p-6 mb-10">
        <div className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent mb-2">
          In the meantime
        </div>
        <p className="font-editorial text-[15px] text-ink-soft leading-relaxed mb-4 max-w-lg">
          You can still take the{' '}
          <strong className="font-medium text-ink not-italic">
            Psychological Safety Assessment
          </strong>{' '}
          — it's open to everyone, no approval needed. Your results save to your
          account.
        </p>
        <Link to="/psychological-safety-assessment" className="btn-text">
          Take the assessment →
        </Link>
      </div>

      <div className="border-t border-rule pt-6 font-editorial italic text-[14px] text-ink-muted flex items-baseline justify-between gap-4 flex-wrap">
        <span>
          Signed in as <strong className="font-medium not-italic text-ink">{userEmail}</strong>
        </span>
        <button
          type="button"
          onClick={() => signOut()}
          className="font-label text-[11px] uppercase tracking-[0.15em] text-link underline underline-offset-2 hover:text-accent not-italic"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function WorkspaceRow({ workspace }: { workspace: MyWorkspace }) {
  const qc = useQueryClient();
  const kindLabel =
    ORG_KINDS.find((k) => k.id === workspace.kind)?.label ?? workspace.kind ?? '';

  /* Warm the React Query cache on hover so the workspace detail page
     renders instantly (or at least skips the ~5-endpoint parallel cold
     fetch). Cheap network fires only if the cache is stale. */
  function prefetch() {
    const id = workspace.id;
    qc.prefetchQuery({
      queryKey: ['workspace', id],
      queryFn: () => apiFetch(`/api/workspaces/${id}`),
      staleTime: 30_000,
    });
    qc.prefetchQuery({
      queryKey: ['workspace-members', id],
      queryFn: () => apiFetch(`/api/workspaces/${id}/members`),
      staleTime: 30_000,
    });
    qc.prefetchQuery({
      queryKey: ['checklists', id],
      queryFn: () => apiFetch(`/api/workspaces/${id}/checklists`),
      staleTime: 30_000,
    });
    qc.prefetchQuery({
      queryKey: ['team', id],
      queryFn: () => apiFetch(`/api/workspaces/${id}/team`),
      staleTime: 30_000,
    });
    qc.prefetchQuery({
      queryKey: ['assessment-aggregate', id],
      queryFn: () => apiFetch(`/api/workspaces/${id}/assessment-aggregate`),
      staleTime: 30_000,
    });
  }

  return (
    <Link
      to={`/account/teams/${workspace.id}`}
      onMouseEnter={prefetch}
      onTouchStart={prefetch}
      onFocus={prefetch}
      className="block bg-paper p-5 hover:bg-paper-deep transition-colors group"
    >
      <div className="flex justify-between items-baseline gap-4 flex-wrap">
        <div>
          <div className="font-editorial text-[20px] font-medium text-ink leading-snug group-hover:text-accent transition-colors">
            {workspace.name}
          </div>
          <div className="font-editorial italic text-[14px] text-ink-muted mt-1">
            {kindLabel}
          </div>
        </div>
        <div className="font-label text-[10px] uppercase tracking-[0.18em] text-accent">
          {workspace.role}
        </div>
      </div>
    </Link>
  );
}

function CenterText({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      {children}
    </div>
  );
}
